import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Copy, QrCode } from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  brandLogo?: string;
  brandName?: string;
}

const QR_COLORS = [
  '#000000', // Black (default)
  '#B91C1C', // Red
  '#DC2626', // Light Red
  '#F3B4D1', // Pink
  '#FDE047', // Yellow
  '#22C55E', // Green
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
];

export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  isOpen,
  onClose,
  url,
  brandLogo,
  brandName
}) => {
  const { t } = useTranslation();
  const [showLogo, setShowLogo] = useState(true);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [qrCode, setQrCode] = useState<QRCodeStyling | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined);

  // Pre-fetch brand logo and convert to data URL to avoid CORS issues
  useEffect(() => {
    if (!isOpen || !brandLogo) {
      setLogoDataUrl(undefined);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          setLogoDataUrl(canvas.toDataURL('image/png'));
        }
      } catch {
        console.warn('Failed to convert brand logo to data URL, QR code will render without logo');
        setLogoDataUrl(undefined);
      }
    };
    img.onerror = () => {
      console.warn('Brand logo failed to load (CORS), QR code will render without logo');
      setLogoDataUrl(undefined);
    };
    img.src = brandLogo;
  }, [isOpen, brandLogo]);

  // Initialize QR code
  useEffect(() => {
    if (!isOpen) return;

    const qrCodeInstance = new QRCodeStyling({
      width: 150,
      height: 150,
      type: 'svg',
      data: url,
      image: showLogo ? logoDataUrl : undefined,
      dotsOptions: {
        color: selectedColor,
        type: 'rounded'
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 5,
        imageSize: 0.4
      },
      backgroundOptions: {
        color: '#ffffff'
      },
      cornersSquareOptions: {
        color: selectedColor,
        type: 'extra-rounded'
      },
      cornersDotOptions: {
        color: selectedColor,
        type: 'dot'
      }
    });

    setQrCode(qrCodeInstance);
  }, [isOpen, url, selectedColor, showLogo, logoDataUrl]);

  // Render QR code
  useEffect(() => {
    if (!qrCode || !qrRef.current || !isOpen) return;

    setIsGenerating(true);
    
    // Clear previous QR code
    qrRef.current.innerHTML = '';
    
    // Append new QR code
    try {
      qrCode.append(qrRef.current);
      setIsGenerating(false);
    } catch (error) {
      console.error('Error rendering QR code:', error);
      setIsGenerating(false);
    }
  }, [qrCode, isOpen]);

  const handleDownload = () => {
    if (!qrCode) return;
    qrCode.download({
      name: `qr-code-${brandName || 'socialness'}-${Date.now()}`,
      extension: 'png'
    });
  };

  const handleCopyImage = async () => {
    if (!qrCode) return;
    
    try {
      const canvas = await qrCode.getRawData('png');
      if (canvas instanceof Blob) {
        const item = new ClipboardItem({ 'image/png': canvas });
        await navigator.clipboard.write([item]);
        // Could add a toast notification here
      }
    } catch (error) {
      console.error('Failed to copy QR code to clipboard:', error);
    }
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
  };

  const handleLogoToggle = () => {
    setShowLogo(!showLogo);
  };

  const handleClose = () => {
    setShowLogo(true);
    setSelectedColor('#000000');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
        <div 
          className="relative bg-white rounded-xl border-2 border-black shadow-xl w-full max-w-lg p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-900">
              <QrCode className="w-5 h-5 text-primary-600" />
              {t('qrCode.title', 'QR Code')}
            </h2>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* QR Code Preview */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                {t('qrCode.preview', 'QR Code Preview')}
                <button className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </button>
              </h3>
            </div>
            
            {/* QR Code Container */}
            <div className="relative bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 p-8 flex items-center justify-center min-h-[175px]">
              {isGenerating && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-6 h-6 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm font-medium">{t('qrCode.generating', 'Generating...')}</span>
                  </div>
                </div>
              )}
              <div ref={qrRef} className="flex items-center justify-center" />
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={handleDownload}
                disabled={isGenerating}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                {t('common.download', 'Download')}
              </button>
              <button
                onClick={handleCopyImage}
                disabled={isGenerating}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Copy className="w-4 h-4" />
                {t('common.copy', 'Copy')}
              </button>
            </div>
          </div>

          {/* Logo Toggle */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                {t('qrCode.logo', 'Logo')}
                <button className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </button>
              </label>
              <button
                onClick={handleLogoToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showLogo ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showLogo ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Color Picker */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('qrCode.color', 'QR Code Color')}
            </label>
            <div className="flex items-center gap-3">
              {/* Color Input */}
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <div 
                  className="w-8 h-8 flex-shrink-0"
                  style={{ backgroundColor: selectedColor }}
                />
                <input
                  type="text"
                  value={selectedColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="px-3 py-2 text-sm font-mono border-none focus:outline-none focus:ring-0 w-24"
                  placeholder="#000000"
                />
              </div>

              {/* Predefined Colors */}
              <div className="flex items-center gap-2">
                {QR_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorChange(color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                      selectedColor === color 
                        ? 'border-gray-900 ring-2 ring-gray-300' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end items-center gap-3 pt-5 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('qrCode.saveChanges', 'Save changes')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 